import { useState } from 'react'
import {
  Card, Table, Button, Space, Typography, Tag, Row, Col,
  Statistic, Alert, List, Badge,
} from 'antd'
import {
  BankOutlined, DashboardOutlined, PlusOutlined,
} from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Title, Text } = Typography
const API = ''

const VOUCHER_TYPE_COLORS: Record<string, string> = {
  PV: 'blue', SV: 'green', JV: 'purple', PYV: 'gold',
  RCV: 'cyan', CV: 'default', DN: 'orange', CN: 'volcano',
}

const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'default', PENDING_APPROVAL: 'warning', POSTED: 'success',
}

export default function AccountingDashboardPage() {
  const { plantId, plants, setPlantId } = usePlantId()
  const qc = useQueryClient()

  const { data: vouchersData, isLoading: vLoading } = useQuery({
    queryKey: ['acc-dashboard-vouchers', plantId],
    queryFn: () =>
      apiClient.get(`${API}/accounting/vouchers`, {
        params: { plant_id: plantId, status: 'DRAFT,PENDING_APPROVAL', limit: 100 },
      }).then(r => r.data),
    enabled: !!plantId,
  })

  const { data: complianceData } = useQuery({
    queryKey: ['acc-compliance-tasks', plantId],
    queryFn: () =>
      apiClient.get(`${API}/accounting/compliance/tasks`, {
        params: { plant_id: plantId },
      }).then(r => r.data),
    enabled: !!plantId,
  })

  const { data: statsData } = useQuery({
    queryKey: ['acc-dashboard-stats', plantId],
    queryFn: () =>
      apiClient.get(`${API}/accounting/dashboard/stats`, {
        params: { plant_id: plantId },
      }).then(r => r.data),
    enabled: !!plantId,
  })

  const { data: coaStatus } = useQuery({
    queryKey: ['coa-status', plantId],
    queryFn: () => apiClient.get(`${API}/accounting/coa-status`, { params: { plant_id: plantId } }).then(r => r.data),
    enabled: !!plantId,
  })

  const setupCoaMut = useMutation({
    mutationFn: () => apiClient.post(`${API}/accounting/setup-coa`, { plant_id: plantId }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['coa-status'] })
    },
  })

  const postMut = useMutation({
    mutationFn: (id: string) =>
      apiClient.post(`${API}/accounting/vouchers/${id}/post`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['acc-dashboard-vouchers'] })
    },
  })

  const vouchers = Array.isArray(vouchersData) ? vouchersData : []
  const complianceTasks = Array.isArray(complianceData) ? complianceData : []
  const stats = statsData ?? {}

  const overdueTasks = complianceTasks.filter((t: any) => t.status === 'OVERDUE')
  const pendingTasks = complianceTasks.filter((t: any) => t.status === 'PENDING')

  const columns = [
    {
      title: 'Voucher No', dataIndex: 'voucher_no', key: 'voucher_no', width: 140,
      render: (v: string) => <b style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</b>,
    },
    {
      title: 'Type', dataIndex: 'voucher_type', key: 'voucher_type', width: 70,
      render: (v: string) => <Tag color={VOUCHER_TYPE_COLORS[v] ?? 'default'}>{v}</Tag>,
    },
    {
      title: 'Date', dataIndex: 'voucher_date', key: 'voucher_date', width: 100,
      render: (v: string) => v ? dayjs(v).format('DD-MMM-YY') : '—',
    },
    {
      title: 'Amount (₹)', dataIndex: 'total_amount', key: 'total_amount', width: 120, align: 'right' as const,
      render: (v: number) => `₹${Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`,
    },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 120,
      render: (v: string) => <Tag color={STATUS_COLORS[v] ?? 'default'}>{v?.replace('_', ' ')}</Tag>,
    },
    {
      title: 'Action', key: 'action', width: 80,
      render: (_: unknown, r: any) =>
        r.status === 'DRAFT' ? (
          <Button
            size="small" type="primary"
            loading={postMut.isPending}
            onClick={() => postMut.mutate(r.id)}
          >Post</Button>
        ) : null,
    },
  ]

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 0 }}>
        <BankOutlined style={{ marginRight: 8, color: '#faad14' }} />
        Finance Control Center
      </Title>
      <Text type="secondary">Accounting overview, pending approvals and compliance status</Text>

      {/* Summary Stats */}
      <Row gutter={16} style={{ marginTop: 16 }}>
        {[
          { title: "Today's Vouchers", value: stats.todays_vouchers ?? 0, color: '#1677ff' },
          { title: 'Pending Approvals', value: stats.pending_approvals ?? 0, color: '#faad14' },
          { title: 'GST Input (Month)', value: `₹${Number(stats.gst_input_month ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`, color: '#52c41a' },
          { title: 'GST Output (Month)', value: `₹${Number(stats.gst_output_month ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`, color: '#13c2c2' },
          { title: 'TDS Deducted (Month)', value: `₹${Number(stats.tds_month ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`, color: '#722ed1' },
          { title: 'Open Compliance Tasks', value: stats.open_compliance ?? 0, color: overdueTasks.length > 0 ? 'red' : '#52c41a' },
        ].map((s, i) => (
          <Col span={4} key={i}>
            <Card size="small">
              <Statistic title={s.title} value={s.value} valueStyle={{ color: s.color, fontSize: 18 }} />
            </Card>
          </Col>
        ))}
      </Row>

      {/* COA Setup Alert */}
      {coaStatus?.setup_required && (
        <Alert
          style={{ marginTop: 16 }}
          type="warning"
          showIcon
          message="Chart of Accounts not set up for this plant"
          description="Click 'Setup COA' to seed all 64 standard ledgers. Required before any accounting entries can be auto-posted."
          action={
            <Button
              size="small" type="primary"
              loading={setupCoaMut.isPending}
              onClick={() => setupCoaMut.mutate()}
            >
              Setup COA
            </Button>
          }
        />
      )}

      {/* Quick Actions */}
      <Card size="small" style={{ marginTop: 16 }}>
        <Space>
          <Button icon={<PlusOutlined />} type="primary" href="/accounting/vouchers">New JV</Button>
          <Button icon={<PlusOutlined />} href="/accounting/vouchers">New Payment</Button>
          <Button icon={<PlusOutlined />} href="/accounting/vouchers">New Receipt</Button>
          <Button icon={<DashboardOutlined />} href="/accounting/fixed-assets">Run Depreciation</Button>
          {coaStatus && !coaStatus.setup_required && (
            <Button onClick={() => setupCoaMut.mutate()} loading={setupCoaMut.isPending}>
              Re-seed COA ({coaStatus.system_ledger_count} ledgers)
            </Button>
          )}
        </Space>
      </Card>

      <Row gutter={16} style={{ marginTop: 16 }}>
        {/* Pending Vouchers */}
        <Col span={16}>
          <Card title="Pending Vouchers" size="small">
            <Table
              dataSource={vouchers}
              columns={columns}
              rowKey="id"
              loading={vLoading}
              size="small"
              pagination={{ pageSize: 10 }}
              scroll={{ x: 640 }}
            />
          </Card>
        </Col>

        {/* Compliance Alerts */}
        <Col span={8}>
          <Card title="Compliance Alerts" size="small">
            {overdueTasks.length > 0 && (
              <Alert
                type="error"
                message={`${overdueTasks.length} overdue task(s)`}
                style={{ marginBottom: 8 }}
                showIcon
              />
            )}
            <List
              size="small"
              dataSource={[...overdueTasks, ...pendingTasks]}
              renderItem={(item: any) => (
                <List.Item>
                  <Space direction="vertical" size={0} style={{ width: '100%' }}>
                    <Space>
                      <Badge
                        status={item.status === 'OVERDUE' ? 'error' : 'warning'}
                        text={<Text strong style={{ fontSize: 12 }}>{item.task_name}</Text>}
                      />
                    </Space>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      Due: {item.due_date ? dayjs(item.due_date).format('DD-MMM-YYYY') : '—'}
                    </Text>
                  </Space>
                </List.Item>
              )}
              locale={{ emptyText: 'No compliance alerts' }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  )
}
